import { useContext, useEffect, useRef, useState } from "react";
import { LessonLang, useT } from "./i18n";
import { LuPlay, LuPause, LuTimer, LuRotateCcw } from "react-icons/lu";
import axios from "../../axios";
import * as styled from "./common.styled";

// Колись тут була підказка при наведенні. Власник попросив без hover — переклад тепер
// через виділення слова (WordPopover у PresentPage), тож поле ua лишається в даних, але не показується.
export function Hint({ children }) {
  return children;
}

// Аудіоплеєр слайда: play/pause, смуга перемотування (клік або перетягування) і час
const fmt = (sec) => {
  if (!Number.isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  return `${m}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;
};

// Аудіо/відео уроків лежать у Cloudflare R2 (стиснуті, заливає lessons-workspace/tools/upload-media.mjs):
// "media/<шлях оригіналу>.mp3" → "<R2>/<шлях>.m4a". Картинки (/lesson-assets/...) — з нашого бекенду.
// REACT_APP_MEDIA_URL=api — тимчасово брати медіа з бекенду (/media), напр. якщо R2 недоступний.
// 2 = 2026-10-02: обрізано оголошення трека (аудіо) і заставку BBC (відео), фрагменти — окремі файли
const MEDIA_VERSION = 2;
const MEDIA_BASE = process.env.REACT_APP_MEDIA_URL || "https://pub-d1b714d91fa3487980aa4bb0e9383705.r2.dev";

export const mediaUrl = (url, part) => {
  if (/^https?:\/\//.test(url)) return url;
  if (!url.startsWith("media/")) return axios.defaults.baseURL + url;
  // Фрагмент треку/відео (start/end) — окремий файл .part<start>-<end> (ріже lessons-workspace/tools/build-media.mjs)
  let rel = url.slice("media/".length).replace(/\.mp3$/i, ".m4a");
  if (part && (part.start != null || part.end != null)) rel = rel.replace(/(\.[a-z0-9]+)$/i, `.part${part.start ?? 0}-${part.end ?? "end"}$1`);
  // ?v= — скидає кеш браузерів/CDN, коли медіа перезібрано з тими самими іменами (збільшити при пересборці);
  // part.v — власна версія треку (уроки es-kids: змінюється, коли трек перезаписано)
  const v = part?.v ?? MEDIA_VERSION;
  return MEDIA_BASE === "api" ? `${axios.defaults.baseURL}media/${rel}?v=${v}` : `${MEDIA_BASE}/${rel}?v=${v}`;
};

// audio.start / audio.end — фрагмент треку; він уже вирізаний в окремий файл (mediaUrl), тож грає з нуля до кінця
export function AudioButton({ audio }) {
  const t = useT();
  const lang = useContext(LessonLang);
  const ref = useRef(null);
  const barRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const start = 0;
  const endOf = (el) => el.duration;

  useEffect(() => {
    const el = ref.current;
    return () => el && el.pause();
  }, []);

  if (!audio?.url) return null;

  const toggle = () => {
    const el = ref.current;
    if (!el.paused) return el.pause();
    if (el.currentTime < start || el.currentTime >= endOf(el) - 0.05) el.currentTime = start;
    el.play();
  };

  // Перемотування: позиція курсора по смузі → секунда фрагмента
  const seekTo = (clientX) => {
    const el = ref.current;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const seek = () => {
      el.currentTime = start + ratio * (endOf(el) - start);
      setTime(el.currentTime - start);
    };
    if (el.duration) seek();
    else {
      el.addEventListener("loadedmetadata", seek, { once: true });
      el.load();
    }
  };

  const onPointerDown = (event) => {
    event.preventDefault();
    seekTo(event.clientX);
    const move = (e) => seekTo(e.clientX);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onTime = (event) => {
    const el = event.currentTarget;
    if (el.currentTime >= endOf(el)) {
      el.pause();
      el.currentTime = endOf(el);
    }
    setTime(Math.max(0, el.currentTime - start));
  };

  return (
    <styled.Audio $playing={playing}>
      <styled.AudioIcon onClick={toggle} title="Play / pause">
        {playing ? <LuPause size={22} /> : <LuPlay size={22} />}
      </styled.AudioIcon>
      <styled.AudioInfo>
        {/* у курсах іншої мови номер треку — внутрішнє імʼя файлу, учню його не показуємо */}
        <span>{audio.track && lang === "en" ? `Track ${audio.track}` : t("Listen")}{audio.part ? ` · ${audio.part}` : ""}</span>
        <styled.AudioTime>
          {fmt(time)} / {fmt(duration)}
        </styled.AudioTime>
      </styled.AudioInfo>
      <styled.AudioBar ref={barRef} onPointerDown={onPointerDown} title="Click or drag to rewind">
        <styled.AudioFill style={{ width: duration ? `${(time / duration) * 100}%` : "0%" }} />
        <styled.AudioKnob style={{ left: duration ? `${(time / duration) * 100}%` : "0%" }} />
      </styled.AudioBar>
      <audio
        ref={ref}
        src={mediaUrl(audio.url, audio)}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(event) => {
          const el = event.currentTarget;
          if (start) el.currentTime = start;
          setDuration(endOf(el) - start);
        }}
        onTimeUpdate={onTime}
      />
    </styled.Audio>
  );
}

// Таймер для говоріння та ігор: клік — старт/пауза, подвійний — скинути
export function Timer({ seconds }) {
  const [left, setLeft] = useState(seconds);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running || left <= 0) return undefined;
    const id = setInterval(() => setLeft((value) => value - 1), 1000);
    return () => clearInterval(id);
  }, [running, left]);

  useEffect(() => {
    if (left <= 0) setRunning(false);
  }, [left]);

  const mm = String(Math.floor(Math.max(left, 0) / 60));
  const ss = String(Math.max(left, 0) % 60).padStart(2, "0");

  return (
    <styled.Timer $done={left <= 0} $running={running}>
      <styled.TimerButton onClick={() => setRunning((value) => !value)} title="Start / pause">
        <LuTimer size={22} />
        {mm}:{ss}
      </styled.TimerButton>
      <styled.TimerReset
        onClick={() => {
          setRunning(false);
          setLeft(seconds);
        }}
        title="Reset"
      >
        <LuRotateCcw size={18} />
      </styled.TimerReset>
    </styled.Timer>
  );
}

// Перемішування зі стабільним результатом для одного й того ж набору (щоб не скакало при рендері)
export const shuffleStable = (items, seed = "") => {
  const score = (value) => {
    let hash = 0;
    const text = seed + JSON.stringify(value);
    for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) | 0;
    return hash;
  };
  const sorted = [...items].sort((a, b) => score(a) - score(b));
  // перемішане не повинно збігатися з правильним порядком (інакше відповідь видно одразу) — зсуваємо на один
  if (sorted.length > 1 && sorted.every((value, i) => value === items[i])) sorted.push(sorted.shift());
  return sorted;
};

// Нотатка вчителю (панель T і вікно «Ключі»). Однорядкова — як була. Багаторядкова — проста розмітка:
// «# …» — заголовок, «## …» — підзаголовок розділу, «- …» — пункт, **…** — жирне.
const bold = (text) => text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <b key={i}>{part}</b> : part));
export function NoteText({ text = "" }) {
  if (!text.includes("\n")) return <>{bold(text)}</>;
  return (
    <styled.NoteText>
      {text.split("\n").map((line, i) => {
        if (!line.trim()) return null;
        if (line.startsWith("## ")) return <h4 key={i}>{bold(line.slice(3))}</h4>;
        if (line.startsWith("# ")) return <h3 key={i}>{bold(line.slice(2))}</h3>;
        if (line.startsWith("- ")) return <styled.NoteItem key={i}>{bold(line.slice(2))}</styled.NoteItem>;
        return <p key={i}>{bold(line)}</p>;
      })}
    </styled.NoteText>
  );
}
